import { type FunctionComponent, useContext } from 'react';
import { type ProductVariantFieldsFragment } from '../../../__generated__/graphql-client-types';
import { VariantContext } from '../../../contexts/variant/variant.context';
import { ZipCodeChanger } from '../../common-components/zip-code-changer/zip-code-changer.component';
import QuickShipIcon from '../../svgs/quick-ship.svg';
import { quickShipIcon } from './shipping-eligibility.css';

export type ShippingEligibilityProps = {
	variant?: ProductVariantFieldsFragment;
	// if true, render markup that allows the user to change their zip code
	showZipCodeChanger?: boolean;
	showOrderedBy?: boolean;
	// Shipping Eligibility for Showroom have a different treatment
	isForShowroom?: boolean;
};

/**
 * ShippingEligibility - renders a shipping estimate and optional zip code changer
 *
 * @param {ShippingEligibilityProps} props
 */
export const ShippingEligibility: FunctionComponent<ShippingEligibilityProps> = ({
	variant,
	showZipCodeChanger = false,
	showOrderedBy,
	isForShowroom = false
}) => {
	const { currentVariant } = useContext(VariantContext);

	const {
		// if the product is eligible for quick ship
		isQuickShip,
		// delivery estimate message
		estimatedDeliveryMessage,
		// a message indicating when an order has to be placed to meet the delivery window
		shippingCutoffMessage,
		shippingCutoffExceedsThreshold,
		shippingMessage,
		hasFreeShipping
	} = variant ? variant : currentVariant;

	const ZipCodeChangerSection = (
		<>
			<span className={'dib mh1'}>-</span>
			<ZipCodeChanger
				zipCodeChangerStyle="INLINE_BLOCK"
				triggerAutomation="change-zip"
				inputAutomation="zip"
				submitButtonAutomation="zip-apply"
				textColor="primary"
			/>
		</>
	);
	return !isForShowroom ? (
		<div className={'f7 f5-ns lh-title'}>
			{isQuickShip && hasFreeShipping ? (
				<>
					<div className={'dib b'}>
						<QuickShipIcon className={`f2 mr1 relative ${quickShipIcon} theme-accent`} data-testid="QuickShipIcon" />
						<span className="theme-grey-darker">FREE Delivery by {estimatedDeliveryMessage}</span>
					</div>
					{showOrderedBy ? (
						<div>
							<div className={`mt1 theme-grey-darker dib`}>
								{shippingCutoffExceedsThreshold ? (
									<span>
										If ordered <strong>Today</strong>
									</span>
								) : (
									<span>
										If ordered in the next <strong>{shippingCutoffMessage}</strong>
									</span>
								)}
							</div>
							{showZipCodeChanger && ZipCodeChangerSection}
						</div>
					) : (
						<span className="mt1">{showZipCodeChanger && ZipCodeChangerSection}</span>
					)}
				</>
			) : (
				<>
					<div className={'b db'}>{shippingMessage}</div>
					<div className={'db'}>
						<div className={'mt1 dib'}>{estimatedDeliveryMessage}</div>

						{showZipCodeChanger && ZipCodeChangerSection}
					</div>
				</>
			)}
		</div>
	) : (
		<ZipCodeChanger
			zipCodeChangerStyle="INLINE_BLOCK"
			zipCodeLabelTransform={(location) => <>Change location from {location?.zipCode}</>}
		/>
	);
};
